import { Inject, Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class FileService implements IFileService {
    constructor(@Inject('fileService') public file: IFileService) {}

    hash(file: File, context?: string): Promise<{ hash: string; context: string }> {
        return this.file.hash(file, context);
    }

    isConvertCompleted(params: { fileItemId: string; fileItemKeyno: string }): Promise<{ isCompleted: boolean; fileName: string; fileItemKeyno: string }> {
        return this.file.isConvertCompleted(params);
    }

    upload(params: FileUploadParams): Promise<{ context: { checksum_md5: string } }> {
        return this.file.upload(params);
    }

    public uploadFiles(params: FilesUploadParamsWithPostprocess): Promise<void>;
    public uploadFiles(params: FilesUploadParamsWithoutPostprocess): Promise<UploadedFile[]>;
    public async uploadFiles(params: FilesUploadParams): Promise<void | UploadedFile[]> {
        const uploadedFiles: UploadedFile[] = [];
        const postprocess = params.postprocess;

        await this.file.uploadFiles({
            ...params,
            postprocess: async (data) => {
                uploadedFiles.push(data);
                await postprocess?.(data);
            },
        });

        return postprocess ? undefined : uploadedFiles;
    }

    download(name: string, config?: DownloadFileParams): Promise<ArrayBuffer> {
        return this.file.download(name, config);
    }

    getFileInfo(params: GetFileInfoParams): Promise<{ fileItemKeyno: number; files: FileInfo[]; total: number }> {
        return this.file.getFileInfo(params);
    }

    getFileList(params: GetFileListParams): Promise<{ files: FileInfo[]; total: number }> {
        return this.file.getFileList(params);
    }

    getFileDetails(params: GetFileDetailsParams): Promise<FileItem> {
        return this.file.getFileDetails(params);
    }

    toFileServerUrl(original: string): string {
        return this.file.toFileServerUrl(original);
    }

    toRelativeUrl(absoluteUrl: string, proxyId: string): string {
        return this.file.toRelativeUrl(absoluteUrl, proxyId);
    }

    update(params: UpdateFileParams): Promise<FileItem> {
        return this.file.update(params);
    }

    delete(params: DeleteFileParams): Promise<void> {
        return this.file.delete(params);
    }

    getTokenLink(imageLink: string): string {
        return this.file.getTokenLink(imageLink);
    }

    getTokenLinkAsync(imageLink: string): string {
        return this.file.getTokenLinkAsync(imageLink);
    }

    isImgContent(contentType: string): boolean {
        return this.file.isImgContent(contentType);
    }

    isOfficeContent(contentType: string): boolean {
        return this.file.isOfficeContent(contentType);
    }

    getFileNameSyntax(argtype: string, argvalue: string): Promise<{ filenametemplate: string; localfilenametemplate: string }> {
        return this.file.getFileNameSyntax(argtype, argvalue);
    }
}

interface IFileService {
    /**
     * Generates a checksum of the given file.
     *
     * @param file the file to generate checksum of.
     * @param context context sent it with the file.
     * @returns a promise containing the hash (checksum) of the file and the context.
     */
    hash(file: File, context?: string): Promise<{ hash: string; context: string }>;

    /**
     * Checks whether the file convert (convert to and from what?) is complete.
     *
     * @param params the parameters used to identify the file to check if convert is complete.
     * @returns a promis containing whether the conver is completed, and the name and id of the file item.
     */
    isConvertCompleted(params: { fileItemId: string; fileItemKeyno: string }): Promise<{ isCompleted: boolean; fileName: string; fileItemKeyno: string }>;

    /**
     * Uploads a file to the fileserver using the given upload parameters.
     *
     * @param params the file and additional settings for the file upload.
     * @retuns a promise returning the context/hash of the newly uploaded file.
     */
    upload(params: FileUploadParams): Promise<{ context: { checksum_md5: string } }>;

    /**
     * Uploads list of files to the file server.
     *
     * @param params the files and additional settings for the files to be uploaded.
     * @returns a promise resolving when the upload has completed for all files.
     */
    uploadFiles(params: FilesUploadParamsWithPostprocess): Promise<void>;
    uploadFiles(params: FilesUploadParamsWithoutPostprocess): Promise<UploadedFile[]>;

    /**
     * Retrieves the arraubuffer of the file with the given base64-encoded filename.
     *
     * @param name the **base64 urlencoded** filename of the file to retrieve array buffer for.
     * @param config additional configuration for the request call.
     */
    download(name: string, config?: DownloadFileParams): Promise<ArrayBuffer>;

    /**
     * Retrieves file info.
     *
     * @param params configuration of file info.
     * @returns a promise containing list of files matching the file info request.
     */
    getFileInfo(params: GetFileInfoParams): Promise<{ fileItemKeyno: number; files: FileInfo[]; total: number }>;

    /**
     * Retrieves list of files matching the given request parameters.
     *
     * @param params request parameters to retrieve file list based of.
     * @returns a promise containing an object with list of total found files.
     */
    getFileList(params: GetFileListParams): Promise<{ files: FileInfo[]; total: number }>;

    /**
     * Retrieves files details for the file matching the given request parameters.
     *
     * @param params the request parameters to retrieve file + file details for.
     */
    getFileDetails(params: GetFileDetailsParams): Promise<FileItem>;

    /**
     * Converts the given url to a url with the file-server as host.
     *
     * @param original the original url to convert.
     */
    toFileServerUrl(original: string): string;

    /**
     * Converts the given absolute url to a url using the current touchtime app as host.
     *
     * @param absoluteUrl the absolute url to convert.
     * @param proxyId the proxy id.
     */
    toRelativeUrl(absoluteUrl: string, proxyId: string): string;

    /**
     * Updates the given file.
     *
     * @param params the request parameters for the file to update.
     * @returns a promise containing the updated file.
     */
    update(params: UpdateFileParams): Promise<FileItem>;

    /**
     * Deletes the file matching the given request parameters.
     *
     * @param params the request parameters for deleting a file.
     */
    delete(params: DeleteFileParams): Promise<void>;

    /**
     * @deprecated
     */
    getTokenLink(imageLink: string): string;

    /**
     * @deprecated
     */
    getTokenLinkAsync(imageLink: string): string;

    /**
     * Check whether the given content-type is of a image type.
     *
     * @param contentType the content type to check.
     * @returns `true` if the given content-type represents a image type, `false` if not.
     */
    isImgContent(contentType: string): boolean;

    /**
     * Check whether the given content-type is of a office type.
     *
     * @param contentType the content type to check.
     * @returns `true` if the given content-type represents a office type, `false` if not.
     */
    isOfficeContent(contentType: string): boolean;

    /**
     * Retrieves the filename syntax for the given argtype and argvalue.
     *
     * @param argtype the argtype to get the filename syntax for.
     * @param argvalue the argvalue to get the filename syntax for.
     * @returns a promise containing the filename template and local filename template.
     */
    getFileNameSyntax(argtype: string, argvalue: string): Promise<{ filenametemplate: string; localfilenametemplate: string }>;
}

interface FileUploadParams {
    /**
     * The file to upload.
     */
    file: File;

    /**
     * The maximum width of the file being uploaded.
     */
    maxWidth?: number;

    /**
     * The max height of the file being uploaded.
     */
    maxHeight?: number;

    /**
     * The file-server url to upload to.
     */
    url?: string;

    /**
     * Function to call on progress for the file upload request.
     *
     * @param evt
     */
    onProgress?: (evt?: unknown) => void;

    /**
     * Additional configuration for the file upload.
     */
    data?: Partial<FileUploadDataParams>;
}

interface FileUploadDataParams {
    /**
     * Whether the upload should replace a file with the same hash/name?.
     */
    replace: boolean;

    /**
     * The maximum width of the file being uploaded.
     */
    maxWidth: number;

    /**
     * The max height of the file being uploaded.
     */
    maxHeight: number;
}

export interface FilesUploadParams {
    files: File[];

    /**
     * The file-server url to upload to.
     */
    url?: string;

    /**
     * Whether the upload should replace a file with the same hash/name?.
     * @default false
     */
    replace?: boolean;
    /**
     * Whether to show a progress indicator as the files are uploaded.
     * @default true
     */
    showProgress?: boolean;

    /**
     * Messages to display when showing progress.
     */
    messages?: Partial<FilesUploadMessages>;

    /**
     * Configuration of templates for the file names.
     */
    fileNameSyntax?: Partial<FilesUploadNameSyntax>;

    preprocess?(fileName: string, file: File): unknown;

    postprocess?(data: UploadedFile): unknown;
}

export type UploadedFile = FileItem & { checksum_md5: string; checksum: string };

export type FilesUploadParamsWithPostprocess = FilesUploadParams & {
    postprocess: NonNullable<FilesUploadParams['postprocess']>;
};

export type FilesUploadParamsWithoutPostprocess = Omit<FilesUploadParams, 'postprocess'> & {
    postprocess?: never;
};

export interface DownloadFileParams {
    /**
     * (Optional) The fileserver url to use, if not provided uses standard.
     */
    url?: string;

    /**
     * Whether to display an error modal when a request fails
     *
     * @default false
     */
    suppressErrorMessage?: boolean;
}

export interface FilesUploadMessages {
    /**
     * Message to display when the list of files is only 1 long.
     */
    single: string;

    /**
     * Message to display when starting to upload multiple files.
     */
    multiInit: string;

    /**
     * Message to display as multiple files are being uploaded.
     */
    multiProgress: string;

    /**
     * Message to display when the upload is complete.
     */
    completed: string;
}

export interface FilesUploadNameSyntax {
    /**
     * Template for the file name.
     */
    filenametemplate: string;

    /**
     * Template for the local file name.
     */
    localfilenametemplate: string;
}

export interface GetFileInfoParams {
    /**
     * The name of the file to get file info for.
     */
    fileName?: string;

    /**
     * List of filenames or object containing filenames to get file info from.
     */
    files?: string[] | { fileName: string }[];
    /**
     * The maximum width of the file.
     */
    maxWidth?: number;

    /**
     * The max height of the file.
     */
    maxHeight?: number;

    /**
     * What page to retireve from the file info.
     *
     * @default 0 which will retrieve all.
     */
    page?: number;

    /**
     * The pagesize to retireve from the file info.
     *
     * @default 0 which will retrieve all.
     */
    pageSize?: number;

    size?: number;

    fileItemKeyno?: number;

    /**
     * @default false
     */
    selectOnlyFromFiles?: boolean;
}

export interface FileInfo {
    fileName: string;
    url: string;
    thumbUrl: string;
    caption: string;
    id: number;
    index: number;
}

/**
 * Represents parameters for deleting a file from the file server.
 */
export interface DeleteFileParams {
    /**
     * The server url to delete from.
     */
    url: string;

    /**
     * Confirm message before deleting the file.
     */
    confirmMessage?: string;

    /**
     * The filename of the file to delete, required unless fileItemKeyno is given.
     */
    fileName?: string;

    /**
     * Theh file item keyno of the file to delete, required unless fileName is given.
     */
    fileItemKeyno?: string;
}

export interface GetFileListParams {
    /**
     * List of filenames, (to retrieve?).
     */
    fileNames?: string[];

    /**
     * Search query for files to retrieve.
     */
    searchTerm?: string;

    /**
     * Max width for the files which are retrieved.
     */
    maxWidth?: number;

    /**
     * Max height for the files which are retrieved.
     */
    maxHeight?: number;

    /**
     * The page to retrieve (not zero-based).
     */
    page?: number;

    /**
     * The size of the pages to retrieve.
     *
     * @default 10
     */
    pageSize?: number;

    /**
     * The file server to use, if not provided uses default.
     */
    url?: string;

    /**
     * @default false
     */
    getAll?: boolean;

    /**
     * @default 'fileItemKeyno'
     */
    orderByColumn?: string;

    /**
     * @default false
     */
    orderByReverse?: boolean;
}

export interface GetFileDetailsParams {
    /**
     * The file item keyno of the file to get details for. Required if fileName is not provided.
     */
    fileItemKeyno?: number;

    /**
     * The file name of the file to get details for. Required if fileItemKeyno is not provided.
     */
    fileName?: string;

    /**
     * The max width to set for the file retrieved.
     */
    maxWidth?: number;
    /**
     * The max height to set for the file retrieved.
     */
    maxHeight?: number;
    /**
     * The file server url to use. Uses default if not provided.
     */
    url?: string;

    /**
     *@default false
     */
    wait?: boolean;

    /**
     * Instance id to send with the call.
     */
    instanceId?: string;

    /**
     * Whether to show a generic error modal if call failed.
     *
     * @default false
     */
    suppressErrorMessage?: boolean;
}

export interface UpdateFileParams {
    /**
     * The file-server url to use, uses default if not provided.
     */
    url?: string;

    /**
     * Confirm message for updating file.
     */
    confirmMessage?: string;

    /**
     * The file-item to update.
     */
    file: FileItem;
}

export interface FileItem {
    fileItemKeyno: null | undefined | number;

    fileItemId: null | undefined | string;

    fileName: null | undefined | string;

    fileNameTemplate: null | undefined | string;

    localFileName: null | undefined | string;

    localFileNameTemplate: null | undefined | string;

    originalFileName: null | undefined | string;

    originalDateTime: null | undefined | string;

    contentType: null | undefined | string;

    size: null | undefined | number;

    latitude: null | undefined | number;

    longitude: null | undefined | number;

    altitude: null | undefined | number;

    orientation: null | undefined | number;

    originalChecksum: null | undefined | string;

    checksum: null | undefined | string;

    isDeleted: null | undefined | boolean;

    deletedTimestamp: null | undefined | string;

    wopiIsConverted: null | undefined | boolean;

    wopiPreConvertFileName: null | undefined | string;

    wopiLockValue: null | undefined | string;

    wopiLockExpires: null | undefined | string;

    wopiOwnerId: null | undefined | string;

    wopiContainer: null | undefined | string;

    wopiVersion: null | undefined | number;

    wopiUserInfo: null | undefined | string;

    fullPath: null | undefined | string;

    createdTimestamp: null | undefined | string;

    updatedTimestamp: null | undefined | string;

    imageLink: null | undefined | string;

    thumbnailLink: null | undefined | string;

    overwrite: null | undefined | boolean;

    isError: null | undefined | boolean;

    errorCode: null | undefined | number;

    message: null | undefined | string;

    orientationDescription: null | undefined | string;

    heading: null | undefined | number;

    itemExists: null | undefined | boolean;

    updatedFileName: null | undefined | boolean;

    replace: null | undefined | boolean;

    userId: null | undefined | string;

    maxWidth: null | undefined | number;

    maxHeight: null | undefined | number;
}
